import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { refreshToken } from "../lib/api/cloud";
import { releaseNotesSchema } from "../lib/release-notes/sanitize-schema";

interface ReleaseNoteSummary {
  slug: string;
  title: string;
  date: string;
  version: string;
}

interface ReleaseNote extends ReleaseNoteSummary {
  body: string;
}

async function fetchWithAuth<T>(url: string): Promise<T | null> {
  const token = localStorage.getItem("cloud_access_token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { headers });

  if (res.status === 401) {
    const refreshed = await refreshToken();
    if (refreshed) {
      res = await fetch(url, {
        headers: { Authorization: `Bearer ${refreshed.accessToken}` },
      });
    }
  }

  if (!res.ok) return null;
  return res.json() as Promise<T>;
}

function formatDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ─── Release Notes List ──────────────────────────────────────────────

function ReleaseNotesList() {
  const { t, i18n } = useTranslation();
  const [notes, setNotes] = useState<ReleaseNoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language?.split("-")[0] ?? "en";

  useEffect(() => {
    setLoading(true);
    fetchWithAuth<ReleaseNoteSummary[]>(`/api/releases/notes?lang=${lang}`)
      .then((data) => {
        if (data) setNotes(data);
      })
      .finally(() => setLoading(false));
  }, [lang]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t("releaseNotes.loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-dark-bg">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-warm-50 mb-3">{t("releaseNotes.title")}</h1>
          <p className="text-warm-400 text-lg">{t("releaseNotes.subtitle")}</p>
        </header>

        {notes.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-warm-500 text-lg">{t("releaseNotes.empty")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => (
              <Link
                key={note.slug}
                to={`/releases/notes/${note.slug}`}
                className="block bg-dark-card border border-dark-border rounded-lg p-6 hover:border-warm-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-warm-50">{note.title}</h2>
                    <time className="text-sm text-warm-500 mt-1 block">
                      {formatDate(note.date, lang)}
                    </time>
                  </div>
                  {note.version && (
                    <span className="text-sm font-mono px-3 py-1 rounded bg-emerald-500/20 text-emerald-400">
                      v{note.version}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Single Release Note ─────────────────────────────────────────────

function ReleaseNoteDetail() {
  const { date } = useParams<{ date: string }>();
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState<ReleaseNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const lang = i18n.language?.split("-")[0] ?? "en";

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setError(false);
    fetchWithAuth<ReleaseNote>(`/api/releases/notes/${date}?lang=${lang}`)
      .then((data) => {
        if (data) setNote(data);
        else setError(true);
      })
      .finally(() => setLoading(false));
  }, [date, lang]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t("releaseNotes.loading")}</p>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex flex-col items-center justify-center gap-4">
        <p className="text-warm-400 text-lg">{t("releaseNotes.notFound", { date })}</p>
        <Link to="/releases/notes" className="text-emerald-400 hover:text-emerald-300 underline">
          {t("releaseNotes.backToAll")}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-dark-bg">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/releases/notes"
          className="inline-flex items-center gap-1 text-warm-500 hover:text-warm-300 text-sm mb-8"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t("releaseNotes.backToAll")}
        </Link>

        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-warm-50">{note.title}</h1>
            {note.version && (
              <span className="text-sm font-mono px-3 py-1 rounded bg-emerald-500/20 text-emerald-400">
                v{note.version}
              </span>
            )}
          </div>
          <time className="text-warm-500">{formatDate(note.date, lang)}</time>
        </header>

        <article
          className="release-note-body prose prose-invert prose-warm max-w-none
          prose-headings:text-warm-100
          prose-p:text-warm-300
          prose-strong:text-warm-100
          prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
          prose-li:text-warm-300
          prose-code:text-amber-300 prose-code:bg-dark-card prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-pre:bg-dark-card prose-pre:border prose-pre:border-dark-border
        "
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
              // 1. rehype-raw reparses raw HTML/SVG that GFM left as-is in the AST.
              //    Without this, inline <svg>/<div> blocks render as literal text.
              rehypeRaw,
              // 2. rehype-sanitize runs AFTER rehype-raw and enforces our allowlist
              //    (SVG elements, inline styles, code-highlight classes). This keeps
              //    us safe even though we author the content ourselves — see
              //    src/lib/release-notes/sanitize-schema.ts.
              [rehypeSanitize, releaseNotesSchema],
            ]}
          >
            {note.body}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}

// ─── Router Entry Point ──────────────────────────────────────────────

export function ReleaseNotes() {
  const { date } = useParams<{ date: string }>();
  return date ? <ReleaseNoteDetail /> : <ReleaseNotesList />;
}
